import { useFetchAgenciesByOrderIdDataContext } from 'modals/ShiftModal/src/views/SendToAgenciesView/SelectAgenciesView/contexts/FetchAgenciesByOrderId.data.context';
import { useFetchMarketplaceCommunitiesByOrderIdDataContext } from 'modals/ShiftModal/src/views/SendToAgenciesView/SelectAgenciesView/contexts/FetchMarketplaceCommunitiesByOrderId.data.context';
import { SSFBehaviorsContextType } from 'hooks/useSSFBehaviors/useSSFBehaviors.types';
import { FC, useCallback, useContext, useMemo } from 'react';
import { AgencyNode } from 'modals/ShiftModal/components/AgencyGridSelect/AgencyGridSelect.types';
import { createGQLDataContext } from 'utils/createDataContext';
import { AgencyCommunityConnection, CommunityConnection } from 'types/graphql';
import { UseFormBehaviorsEventType } from 'hooks/useFormBehaviors/useFormBehaviors.types';

interface IFetchCombinedCommunitiesByOrderIdData {
  combinedNodes?: AgencyNode[];
  fetchMarketplaceCommunities?: CommunityConnection;
  fetchAgencyCommunities?: AgencyCommunityConnection;
}

export const FetchCombinedCommunitiesByOrderIdDataContext = createGQLDataContext<
  SSFBehaviorsContextType<IFetchCombinedCommunitiesByOrderIdData>
>({} as SSFBehaviorsContextType<IFetchCombinedCommunitiesByOrderIdData>);

export const FetchCombinedCommunitiesByOrderIdDataProvider: FC = ({ children }) => {
  const marketplaceContext = useFetchMarketplaceCommunitiesByOrderIdDataContext();
  const agencyContext = useFetchAgenciesByOrderIdDataContext();

  const marketplaceData = marketplaceContext.data?.fetchMarketplaceCommunities;
  const agencyData = agencyContext.data?.fetchAgencyCommunities;

  const marketplaceNodes = useMemo(
    () => (marketplaceData?.nodes || []).map((node) => ({ ...node, sourceType: 'marketplace' })),
    [marketplaceData?.nodes],
  );

  const agencyNodes = useMemo(
    () => (agencyData?.nodes || []).map((node) => ({ ...node, sourceType: 'agency' })),
    [agencyData?.nodes],
  );

  const combinedNodes = useMemo(() => {
    if (marketplaceData?.pageInfo?.hasNextPage) {
      return marketplaceNodes;
    }
    return [...marketplaceNodes, ...agencyNodes];
  }, [marketplaceNodes, agencyNodes, marketplaceData?.pageInfo?.hasNextPage]);

  const loadMore = useMemo(() => {
    if (marketplaceData?.pageInfo?.hasNextPage) {
      return marketplaceContext.loadMore;
    }
    if (agencyData?.pageInfo?.hasNextPage) {
      return agencyContext.loadMore;
    }
    return () => {};
  }, [
    marketplaceData?.pageInfo?.hasNextPage,
    marketplaceContext.loadMore,
    agencyData?.pageInfo?.hasNextPage,
    agencyContext.loadMore,
  ]);

  const onChange = useCallback(
    (e: UseFormBehaviorsEventType) => {
      marketplaceContext.onChange(e);
      agencyContext.onChange(e);
    },
    [marketplaceContext, agencyContext],
  );

  const onReset = useCallback(() => {
    marketplaceContext.onReset();
    agencyContext.onReset();
  }, [marketplaceContext, agencyContext]);

  const onRefetch = useCallback(
    (resetPagination?: boolean) => {
      marketplaceContext.onRefetch(resetPagination);
      agencyContext.onRefetch(resetPagination);
    },
    [marketplaceContext, agencyContext],
  );

  const contextValue = useMemo(
    () => ({
      ...marketplaceContext,
      data: {
        combinedNodes: combinedNodes as AgencyNode[],
        fetchMarketplaceCommunities: marketplaceData,
        fetchAgencyCommunities: agencyData,
      },
      loadMore,
      onChange,
      onReset,
      onRefetch,
    }),
    [
      marketplaceContext,
      marketplaceData,
      agencyData,
      combinedNodes,
      loadMore,
      onChange,
      onReset,
      onRefetch,
    ],
  );

  return (
    <FetchCombinedCommunitiesByOrderIdDataContext.Provider value={contextValue}>
      {children}
    </FetchCombinedCommunitiesByOrderIdDataContext.Provider>
  );
};

export const useFetchCombinedCommunitiesByOrderIdDataContext = () =>
  useContext(FetchCombinedCommunitiesByOrderIdDataContext);
